import { create } from 'zustand';
import type { Toast, ToastVariant } from '@/types/common.types';

interface ToastState {
  toasts: Toast[];
}

interface ToastActions {
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

export type ToastStore = ToastState & ToastActions;

let counter = 0;

export const useToastStore = create<ToastStore>()((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const existing = get().toasts;
    // Same variant+title collapses onto the live toast rather than stacking a
    // second banner. Refresh its payload in place: skipping the update left a
    // stale description on screen — rescheduling a task by a week and then by a
    // month kept showing "1 week", and the toast expired on the first click's
    // timer. Widening the key to include the description is not the fix; that
    // stacks two banners, which is the crowding this is here to avoid.
    //
    // The id is deliberately preserved. Callers hold the returned id across async
    // work and dismiss by it later (see hooks/useAsyncExport.tsx, which keeps it
    // in a ref through a poll loop on a duration:0 toast); minting a fresh one
    // would strand that toast on screen forever.
    const duplicate = existing.find((t) => t.variant === toast.variant && t.title === toast.title);
    if (duplicate) {
      set({
        toasts: existing.map((t) => (t.id === duplicate.id ? { ...toast, id: duplicate.id } : t)),
      });
      return duplicate.id;
    }
    const id = `toast-${++counter}-${Date.now()}`;
    set({ toasts: [...existing, { ...toast, id }] });
    return id;
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearAll: () => set({ toasts: [] }),
}));

// Convenience helper — call outside of React components (e.g., in API error handlers)
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().addToast({ variant: 'success', title, description, duration: 3000 }),

  error: (title: string, description?: string) =>
    useToastStore.getState().addToast({ variant: 'error', title, description, duration: 5000 }),

  warning: (title: string, description?: string) =>
    useToastStore.getState().addToast({ variant: 'warning', title, description, duration: 5000 }),

  info: (title: string, description?: string) =>
    useToastStore.getState().addToast({ variant: 'info', title, description }),

  custom: (t: Omit<Toast, 'id'>) => useToastStore.getState().addToast(t),

  dismiss: (id: string) => useToastStore.getState().removeToast(id),
};

// Re-export variant type for convenience
export type { ToastVariant };
